import React, { useRef, useEffect, useState } from 'react';
import { Perspective } from '@feature/event-detail/types';
import { PerspectiveCard } from './PerspectiveCard';

interface Props {
  perspectives: Perspective[];
  onPerspectiveTap: (perspective: Perspective) => void;
}

export function PerspectivesCarousel({ perspectives, onPerspectiveTap }: Props): React.ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollPositionRef.current = container.scrollLeft;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && scrollPositionRef.current > 0) {
      container.scrollLeft = scrollPositionRef.current;
    }
  }, [perspectives]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  if (perspectives.length === 0) {
    return (
      <div style={styles.emptyStateContainer}>
        <div style={styles.emptyIconWrapper}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="#9ca3af">
            <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>
          </svg>
        </div>
        <h3 style={styles.emptyTitle}>No live perspectives yet</h3>
        <p style={styles.emptySubtitle}>
          Check back soon to see live perspectives from this event
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div
        ref={scrollContainerRef}
        className="perspectives-carousel-scroll"
        style={styles.scrollContainer}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {perspectives.map((perspective) => (
          <div key={perspective.id} style={styles.cardContainer}>
            <PerspectiveCard
              perspective={perspective}
              onTap={() => onPerspectiveTap(perspective)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    width: '100%',
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingLeft: '16px',
    paddingRight: '16px',
    height: '100%',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    cursor: 'grab',
    userSelect: 'none',
  },
  cardContainer: {
    height: '100%',
    minHeight: '220px',
    aspectRatio: '9 / 16',
    marginRight: '16px',
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '32px',
  },
  emptyIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '40px',
    marginBottom: '16px',
  },
  emptyTitle: {
    color: '#374151',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    textAlign: 'center',
    margin: '0 0 8px 0',
  },
  emptySubtitle: {
    color: '#6b7280',
    fontSize: '14px',
    lineHeight: '20px',
    textAlign: 'center',
    margin: 0,
  },
};
